import type React from "react"
import { 
  IconArgentina, 
  IconBolivia, 
  IconBrazil, 
  // IconCanada, 
  IconChile, 
  IconColombia, 
  IconEcuador, 
  IconMexico, 
  IconParaguay, 
  IconPeru, 
  IconUSA, 
  IconUruguay, 
  IconVenezuela,
  IconGuatemala,
} from "../assets/icons/icons"
import { dictionary, Language } from "../utils/dictionary"

// ISO 3166-1 ALPHA-2 Country Codes
export enum Country {
  ARGENTINA = "AR",
  BOLIVIA = "BO",
  BRAZIL = "BR",
  CANADA = "CA",
  CHILE = "CL",
  COLOMBIA = "CO",
  ECUADOR = "EC",
  MEXICO = "MX",
  PARAGUAY = "PY",
  PERU = "PE",
  UNITED_STATES = "US",
  URUGUAY = "UY",
  GUATEMALA = "GT",
  VENEZUELA = "VE",
}

// ISO 4217 Currency Codes
export enum Currency {
  ARS = "ARS", // Argentine Peso
  BOB = "BOB", // Bolivian Boliviano
  BRL = "BRL", // Brazilian Real
  CAD = "CAD", // Canadian Dollar
  CLP = "CLP", // Chilean Peso
  COP = "COP", // Colombian Peso
  USD = "USD", // US Dollar
  MXN = "MXN", // Mexican Peso
  PYG = "PYG", // Paraguayan Guaraní
  PEN = "PEN", // Peruvian Sol
  UYU = "UYU", // Uruguayan Peso
  VES = "VES", // Venezuelan Bolívar
  GTQ = "GTQ", // Guatemalan Quetzal
}

export const getCountriesList = (language: Language = Language.SPANISH) => {
  const allowed = [
    "argentina",
    "bolivia",
    "brazil",
    "canada",
    "chile",
    "colombia",
    "ecuador",
    "mexico",
    "paraguay",
    "peru",
    "united-states",
    "uruguay",
    "venezuela",
    "guatemala",
  ]

  const countryIconsMap: Record<string, React.ComponentType<any> | null> = {
    "argentina": IconArgentina,
    "bolivia": IconBolivia,
    "brazil": IconBrazil,
    "canada": null,
    "chile": IconChile,
    "colombia": IconColombia,
    "ecuador": IconEcuador,
    "mexico": IconMexico,
    "paraguay": IconParaguay,
    "peru": IconPeru,
    "united-states": IconUSA,
    "uruguay": IconUruguay,
    "venezuela": IconVenezuela,
    "guatemala": IconGuatemala,
  }

  const allCountries = [
    {
      name: dictionary["argentina"][language],
      flag: "🇦🇷",
      icon: countryIconsMap["argentina"],
      currency: Currency.ARS,
      language: Language.SPANISH,
      country: Country.ARGENTINA,
      dialCode: "+54",
    },
    {
      name: dictionary["bolivia"][language],
      flag: "🇧🇴",
      icon: countryIconsMap["bolivia"],
      currency: Currency.BOB,
      language: Language.SPANISH,
      country: Country.BOLIVIA,
      dialCode: "+591",
    },
    {
      name: dictionary["brazil"][language],
      flag: "🇧🇷",
      icon: countryIconsMap["brazil"],
      currency: Currency.BRL,
      language: Language.PORTUGUESE,
      country: Country.BRAZIL,
      dialCode: "+55",
    },
    {
      name: dictionary["canada"][language],
      flag: "🇨🇦",
      icon: countryIconsMap["canada"],
      currency: Currency.CAD,
      language: Language.ENGLISH,
      country: Country.CANADA,
      dialCode: "+1",
    },
    {
      name: dictionary["chile"][language],
      flag: "🇨🇱",
      icon: countryIconsMap["chile"],
      currency: Currency.CLP,
      language: Language.SPANISH,
      country: Country.CHILE,
      dialCode: "+56",
    },
    {
      name: dictionary["colombia"][language],
      flag: "🇨🇴",
      icon: countryIconsMap["colombia"],
      currency: Currency.COP,
      language: Language.SPANISH,
      country: Country.COLOMBIA,
      dialCode: "+57",
    },
    {
      name: dictionary["ecuador"][language],
      flag: "🇪🇨",
      icon: countryIconsMap["ecuador"],
      currency: Currency.USD,
      language: Language.SPANISH,
      country: Country.ECUADOR,
      dialCode: "+593",
    },
    {
      name: dictionary["guatemala"][language],
      flag: "🇬🇹",
      icon: countryIconsMap["guatemala"],
      currency: Currency.GTQ,
      language: Language.SPANISH,
      country: Country.GUATEMALA,
      dialCode: "+502",
    },
    {
      name: dictionary["mexico"][language],
      flag: "🇲🇽",
      icon: countryIconsMap["mexico"],
      currency: Currency.MXN,
      language: Language.SPANISH,
      country: Country.MEXICO,
      dialCode: "+52",
    },
    {
      name: dictionary["paraguay"][language],
      flag: "🇵🇾",
      icon: countryIconsMap["paraguay"],
      currency: Currency.PYG,
      language: Language.SPANISH,
      country: Country.PARAGUAY,
      dialCode: "+595",
    },
    {
      name: dictionary["peru"][language],
      flag: "🇵🇪",
      icon: countryIconsMap["peru"],
      currency: Currency.PEN,
      language: Language.SPANISH,
      country: Country.PERU,
      dialCode: "+51",
    },
    {
      name: dictionary["united-states"][language],
      flag: "🇺🇸",
      icon: countryIconsMap["united-states"],
      currency: Currency.USD,
      language: Language.ENGLISH,
      country: Country.UNITED_STATES,
      dialCode: "+1",
    },
    {
      name: dictionary["uruguay"][language],
      flag: "🇺🇾",
      icon: countryIconsMap["uruguay"],
      currency: Currency.UYU,
      language: Language.SPANISH,
      country: Country.URUGUAY,
      dialCode: "+598",
    },
    {
      name: dictionary["venezuela"][language],
      flag: "🇻🇪",
      icon: countryIconsMap["venezuela"],
      currency: Currency.VES,
      language: Language.SPANISH,
      country: Country.VENEZUELA,
      dialCode: "+58",
    },
  ]

  return allCountries
    .filter((c) => allowed.includes(Object.keys(dictionary).find((key) => dictionary[key][language] === c.name) || ""))
    .sort((a, b) => a.name.localeCompare(b.name))
}


export const getLanguagesList = (language: Language = Language.SPANISH) => [
  { name: dictionary["spanish"][language], code: Language.SPANISH },
  { name: dictionary["english"][language], code: Language.ENGLISH },
  { name: dictionary["portuguese"][language], code: Language.PORTUGUESE },
]
